import { useEffect, useRef, useState } from 'react';
import {
  subjectsService,
  type Subject,
  type SubjectRoleType,
} from '@/services/subjects.service';

interface Props {
  value: Subject | null;
  onChange: (subject: Subject | null) => void;
}

const ROLE_TYPES: { value: SubjectRoleType; label: string }[] = [
  { value: 'OTHER', label: 'Other' },
  { value: 'SUSPECT', label: 'Suspect' },
  { value: 'VICTIM', label: 'Victim' },
  { value: 'WITNESS', label: 'Witness' },
  { value: 'LAW_ENFORCEMENT', label: 'Law Enforcement' },
  { value: 'REPORTING_PARTY', label: 'Reporting Party' },
];

export default function SubjectPicker({ value, onChange }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Subject[]>([]);
  const [searching, setSearching] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    firstName: '',
    lastName: '',
    roleType: 'OTHER' as SubjectRoleType,
    email: '',
    phone: '',
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (value !== null) {
      return;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (!query.trim()) {
      setResults([]);
      return;
    }
    let alive = true;
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      void subjectsService
        .getSubjects({ search: query.trim(), limit: 10 })
        .then((res) => {
          if (alive) {
            setResults(res.subjects);
          }
        })
        .catch(() => {
          if (alive) {
            setResults([]);
          }
        })
        .finally(() => {
          if (alive) {
            setSearching(false);
          }
        });
    }, 250);
    return () => {
      alive = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, value]);

  if (value) {
    const roleConfig = subjectsService.getRoleTypeConfig(value.roleType);
    const riskConfig = subjectsService.getRiskLevelConfig(value.riskLevel);
    return (
      <div className="flex items-center justify-between gap-3 rounded border border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/50 p-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
            {subjectsService.getSubjectDisplayName(value)}
          </p>
          <div className="mt-1 flex items-center gap-2 text-xs">
            <span
              className={`px-1.5 py-0.5 rounded ${subjectsService.getBadgeClasses(roleConfig.color)}`}
            >
              {roleConfig.label}
            </span>
            <span
              className={`px-1.5 py-0.5 rounded ${subjectsService.getBadgeClasses(riskConfig.color)}`}
            >
              {riskConfig.label} risk
            </span>
            {value.email && (
              <span className="text-secondary-500 truncate">{value.email}</span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs text-primary-600 hover:underline"
        >
          Change
        </button>
      </div>
    );
  }

  if (showCreate) {
    const canSubmit =
      createForm.firstName.trim().length > 0 && createForm.lastName.trim().length > 0;
    return (
      <div className="rounded border border-secondary-200 dark:border-secondary-700 p-3 space-y-3">
        <p className="text-xs font-semibold text-secondary-700 dark:text-secondary-200">
          Create new subject
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-secondary-700 dark:text-secondary-200">
            First name
            <input
              aria-label="First name"
              className="mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800"
              value={createForm.firstName}
              onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
            />
          </label>
          <label className="text-xs text-secondary-700 dark:text-secondary-200">
            Last name
            <input
              aria-label="Last name"
              className="mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800"
              value={createForm.lastName}
              onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
            />
          </label>
          <label className="text-xs text-secondary-700 dark:text-secondary-200">
            Role
            <select
              aria-label="Role"
              className="mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800"
              value={createForm.roleType}
              onChange={(e) =>
                setCreateForm({ ...createForm, roleType: e.target.value as SubjectRoleType })
              }
            >
              {ROLE_TYPES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-secondary-700 dark:text-secondary-200">
            Email
            <input
              aria-label="Email"
              type="email"
              className="mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
            />
          </label>
          <label className="text-xs text-secondary-700 dark:text-secondary-200 col-span-2">
            Phone
            <input
              aria-label="Phone"
              className="mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800"
              value={createForm.phone}
              onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
            />
          </label>
        </div>
        {createError && <p className="text-xs text-danger-600">{createError}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setShowCreate(false);
              setCreateError(null);
            }}
            disabled={creating}
            className="px-3 py-1.5 text-sm rounded border border-secondary-300 dark:border-secondary-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit || creating}
            onClick={async () => {
              setCreating(true);
              setCreateError(null);
              try {
                const subject = await subjectsService.createSubject({
                  firstName: createForm.firstName.trim(),
                  lastName: createForm.lastName.trim(),
                  roleType: createForm.roleType,
                  email: createForm.email.trim() || undefined,
                  phone: createForm.phone.trim() || undefined,
                });
                onChange(subject);
                setShowCreate(false);
                setQuery('');
              } catch (err: unknown) {
                setCreateError(err instanceof Error ? err.message : 'Failed to create subject');
              } finally {
                setCreating(false);
              }
            }}
            className="px-3 py-1.5 text-sm rounded bg-primary-600 text-white disabled:opacity-60"
          >
            {creating ? 'Creating…' : 'Create & Select'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        type="text"
        placeholder="Search subjects or create new…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="w-full rounded border p-2 bg-white dark:bg-secondary-800"
      />
      {(query.trim().length > 0 || searching) && (
        <ul className="absolute z-10 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-900 shadow-lg">
          {searching && (
            <li className="px-3 py-2 text-xs text-secondary-500">Searching…</li>
          )}
          {!searching && results.length === 0 && (
            <li className="px-3 py-2 text-xs text-secondary-500">No matching subjects</li>
          )}
          {results.map((s) => {
            const roleConfig = subjectsService.getRoleTypeConfig(s.roleType);
            return (
              <li
                key={s.id}
                role="option"
                aria-selected={false}
                onClick={() => onChange(s)}
                className="cursor-pointer px-3 py-2 text-sm hover:bg-secondary-50 dark:hover:bg-secondary-800"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{subjectsService.getSubjectDisplayName(s)}</span>
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded ${subjectsService.getBadgeClasses(roleConfig.color)}`}
                  >
                    {roleConfig.label}
                  </span>
                </div>
              </li>
            );
          })}
          <li
            onClick={() => setShowCreate(true)}
            className="cursor-pointer border-t border-secondary-200 dark:border-secondary-700 px-3 py-2 text-sm text-primary-600 hover:bg-secondary-50 dark:hover:bg-secondary-800"
          >
            + Create new subject
          </li>
        </ul>
      )}
    </div>
  );
}
